import * as React from 'react';
import { ChevronRight, Truck, User } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { formatTimeWindowDotted } from '@klareospace/pharos-lib-front-utils';
import { isDefined, resolveDriverName, resolveTaskTimeSlot } from '@klareospace/pharos-lib-utils';
import { type Task } from '@klareospace/pharos-lib-types';
import {
  CardList,
  DrawerSection,
  EntityIcon,
  ErrorBanner,
  InlineLabel,
  OrderStatusBadge,
  Skeleton,
} from '@klareospace/pharos-lib-web-utils';

import { CountPill } from '&/features/orders/components/count-pill';
import { composeStatusErrorMessage, TASK_ERROR_STATUSES } from '&/features/orders/utils';
import { PersonIcon } from '&/shared/components';

import { hasFailureComment, resolveFailureAttribution } from '../utils/task-failure';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TasksSectionProps {
  readonly isLoading: boolean;
  /** Called with the clicked task, when provided. Rows become clickable when set. */
  readonly onTaskClick?: (task: Task) => void;
  readonly tasks: Task[] | undefined;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TasksSection({ isLoading, onTaskClick, tasks }: TasksSectionProps): React.JSX.Element {
  const { t } = useTranslation('tasks');
  const tOrders = (key: string): string => t(`orders:${key}`);
  const total = tasks?.length ?? 0;
  const title = `${t('list.title')} (${total})`;

  /** "Reported by {name} · {timestamp}" attribution line, omitting missing segments. */
  function buildAttributionRow(task: Task): React.ReactNode {
    const { driverName, timestamp } = resolveFailureAttribution(task);
    if (driverName === undefined && timestamp === undefined) return undefined;

    return (
      <p className="text-xs text-muted-foreground">
        {driverName !== undefined && (
          <Trans
            components={{ driver: <span /> }}
            i18nKey="failure.reportedBy"
            ns="tasks"
            values={{ name: driverName }}
          />
        )}
        {driverName !== undefined && timestamp !== undefined && ' · '}
        {timestamp}
      </p>
    );
  }

  /** Richer footer content for a failed task with a driver-entered comment. */
  function buildFailureCommentContent(task: Task, headline: string): React.ReactNode {
    return (
      <div className="min-w-0 space-y-1">
        <p className="text-sm text-destructive">{headline}</p>
        <p className="text-sm text-destructive/90 italic break-words">“{task.statusReasonMessage?.trim()}”</p>
        {buildAttributionRow(task)}
      </div>
    );
  }

  /** Resolves the error banner footer for tasks in a failure-related status. */
  function buildTaskFooter(task: Task): React.ReactNode {
    if (!TASK_ERROR_STATUSES.has(task.status)) return undefined;
    const headline = composeStatusErrorMessage(tOrders, t(`errors.${task.status}`), task.statusReason ?? undefined);
    if (!hasFailureComment(task)) return <ErrorBanner message={headline} />;
    return <ErrorBanner message={buildFailureCommentContent(task, headline)} />;
  }

  if (isLoading) {
    return (
      <DrawerSection icon={Truck} title={title}>
        <Skeleton className="h-12 w-full rounded-xl" />
      </DrawerSection>
    );
  }

  if (tasks === undefined || tasks.length === 0) {
    return (
      <DrawerSection icon={Truck} title={title}>
        <span className="text-sm text-muted-foreground">{t('list.empty')}</span>
      </DrawerSection>
    );
  }

  const rows = tasks.map((task, i) => {
    const taskTimeSlot = resolveTaskTimeSlot(task);
    const driverName = resolveDriverName(task.driver) ?? null;
    const footer = buildTaskFooter(task);

    return {
      cells: [
        {
          content: (
            <CountPill>
              {i + 1}/{total}
            </CountPill>
          ),
        },
        {
          content: <span className="text-xs truncate text-foreground capitalize">{task.type}</span>,
        },
        {
          content: (
            <span className="text-xs text-muted-foreground truncate">
              {isDefined(taskTimeSlot) ? formatTimeWindowDotted(taskTimeSlot) : '—'}
            </span>
          ),
        },
        {
          content: (
            <div className="min-w-0">
              {task.carrier && (
                <InlineLabel
                  labelClassName="text-foreground"
                  prefix={<EntityIcon alt={task.carrier.name} size="xs" />}
                  size="xs"
                >
                  {task.carrier.name}
                </InlineLabel>
              )}
              {driverName !== null ? (
                <InlineLabel
                  className="mt-1"
                  labelClassName="text-foreground"
                  prefix={<PersonIcon name={driverName} size="xs" />}
                  size="xs"
                >
                  {driverName}
                </InlineLabel>
              ) : (
                <div className="flex items-center gap-1.5 mt-1">
                  <User className="w-3 h-3 text-muted-foreground shrink-0" />
                  <span className="text-xs text-muted-foreground italic">{t('noDriver')}</span>
                </div>
              )}
            </div>
          ),
        },
        {
          content: <OrderStatusBadge label={tOrders(`status.${task.status}`)} status={task.status} />,
        },
        {
          content: <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />,
        },
      ],
      footer,
      id: task.id,
      onClick: onTaskClick ? (): void => onTaskClick(task) : undefined,
    };
  });

  return (
    <DrawerSection icon={Truck} title={title}>
      <CardList gridTemplateColumns="auto 1fr 1fr 1fr auto auto" rows={rows} />
    </DrawerSection>
  );
}
